import { ReportRelationships, ReportStatus } from 'dashboard/models/report';
import {
  ReportAddressLookup,
  ReportAddressLookupAttributes,
} from 'dashboard/models/report/address-lookup';
import {
  RelatedSource,
  ReportAdverseMedia,
  ReportAdverseMediaAttributes,
} from 'dashboard/models/report/adverse-media';
import {
  ReportBusinessAdverseMedia,
  ReportBusinessAdverseMediaAttributes,
} from 'dashboard/models/report/business-adverse-media';
import {
  ReportBusinessAssociatedPersons,
  ReportBusinessAssociatedPersonsAttributes,
} from 'dashboard/models/report/business-associated-persons';
import {
  ReportBusinessClassification,
  ReportBusinessClassificationAttributes,
} from 'dashboard/models/report/business-classification';
import {
  ReportBusinessIndustryClassification,
  ReportBusinessIndustryClassificationAttributes,
} from 'dashboard/models/report/business-industry-classification';
import {
  FilingStatus,
  ReportBusinessLiensLookup,
  ReportBusinessLiensLookupAttributes,
} from 'dashboard/models/report/business-liens-lookup';
import {
  MatchStatus,
  ReportBusinessLookup,
  ReportBusinessLookupAttributes,
} from 'dashboard/models/report/business-lookup';
import { BusinessIdentifierType } from 'dashboard/models/report/business-lookup-result-edge';
import {
  CaliforniaAttorneyGeneralStatus,
  ReportBusinessNonprofit,
  ReportBusinessNonprofitAttributes,
} from 'dashboard/models/report/business-nonprofit';
import {
  ReportBusinessOnlinePresence,
  ReportBusinessOnlinePresenceAttributes,
} from 'dashboard/models/report/business-online-presence';
import {
  ReportBusinessRegistrationsLookup,
  ReportBusinessRegistrationsLookupAttributes,
} from 'dashboard/models/report/business-registrations-lookup';
import {
  ReportBusinessSocialMedia,
  ReportBusinessSocialMediaAttributes,
} from 'dashboard/models/report/business-social-media';
import {
  BusinessWatchlistType,
  ReportBusinessWatchlist,
  ReportBusinessWatchlistAttributes,
} from 'dashboard/models/report/business-watchlist';
import {
  BusinessWebsiteMatchStatus,
  ReportBusinessWebsite,
  ReportBusinessWebsiteAttributes,
} from 'dashboard/models/report/business-website';
import {
  ReportCryptoAddressWatchlist,
  ReportCryptoAddressWatchlistAttributes,
} from 'dashboard/models/report/crypto-address-watchlist';
import { ReportCustomList } from 'dashboard/models/report/custom-list';
import {
  EmailAddressReputation,
  EmailDomainReputation,
  ReportEmailAddress,
  ReportEmailAddressAttributes,
} from 'dashboard/models/report/email-address';
import {
  FinraBrokerCheckBrokerStatus,
  FinraBrokerCheckBrokerType,
  ReportFinraBrokerCheck,
  ReportFinraBrokerCheckAttributes,
} from 'dashboard/models/report/finra-broker-check';
import { ReportChainalysisAddressScreening } from 'dashboard/models/report/integration/chainalysis-address-screening';
import { ReportMxAccount, ResultStatus } from 'dashboard/models/report/integration/mx-account';
import {
  ReportSentilinkApplicationRisk,
  SentilinkEnvironmentType,
} from 'dashboard/models/report/integration/sentilink-application-risk';
import { ReportSentilinkScores } from 'dashboard/models/report/integration/sentilink-scores';
import {
  ReportPhoneNumber,
  ReportPhoneNumberAttributes,
} from 'dashboard/models/report/phone-number';
import {
  ReportPoliticallyExposedPerson,
  ReportPoliticallyExposedPersonAttributes,
} from 'dashboard/models/report/politically-exposed-person';
import { ReportProfile, ReportProfileAttributes } from 'dashboard/models/report/profile';
import {
  ReportProfileNonAuthoritative,
  ReportProfileNonAuthoritativeAttributes,
} from 'dashboard/models/report/profile-non-authoritative';
import {
  ReportSecActionLookup,
  ReportSecActionLookupAttributes,
} from 'dashboard/models/report/sec-action-lookup';
import {
  ReportSocialMedia,
  ReportSocialMediaAttributes,
} from 'dashboard/models/report/social-media';
import { ReportSynthetic, ReportSyntheticAttributes } from 'dashboard/models/report/synthetic';
import {
  ReportWatchlist,
  ReportWatchlistAttributes,
  WatchlistType,
} from 'dashboard/models/report/watchlist';
import { ReportRunMatchStatus } from 'dashboard/models/report-run';
import { ApiModel } from 'lib/interfaces/api';

const createdAt = '2023-01-01T00:00:00.000Z';
const completedAt = '2023-01-01T00:01:00.000Z';
const scheduledAt = '2023-01-01T00:01:00.000Z';

export function createPreviewReportEmailAddress(
  attributes?: Partial<ReportEmailAddressAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportEmailAddress {
  return {
    attributes: {
      'primary-input': 'email@domain.com',
      status: ReportStatus.Ready,
      'created-at': createdAt,
      'completed-at': completedAt,
      'email-address': 'email@domain.com',
      'email-reputation': EmailAddressReputation.HIGH,
      'email-reference-count': 11,
      'email-domain-reputation': EmailDomainReputation.LOW,
      'email-domain-age-days': 6503,
      'email-first-seen-days': 1034,
      'email-last-seen-days': 123,
      'email-is-suspicious': false,
      'email-is-blocklisted': false,
      'email-is-disposable': false,
      'email-is-spam': true,
      'email-is-deliverable': true,
      'email-is-valid-mx': true,
      'email-domain-exists': true,
      'email-is-free-provider': false,
      'email-domain': 'domain.com',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_123',
    type: ApiModel.ReportEmailAddress,
  };
}

export function createPreviewReportFinraBrokerCheck(
  attributes?: Partial<ReportFinraBrokerCheckAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportFinraBrokerCheck {
  return {
    attributes: {
      'primary-input': 'ONECHRONOS',
      status: ReportStatus.Ready,
      'created-at': createdAt,
      'completed-at': completedAt,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'redacted-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'next-scheduled-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'reference-id': null,
      'broker-type': FinraBrokerCheckBrokerType.Firm,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'crd-number': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'name-full': null,
      'firm-name': 'ONECHRONOS',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'address-city': null,
      'address-subdivision': 'NY',
      result: [
        {
          'crd-number': '286322',
          name: 'ONECHRONOS',
          'other-names': ['ONECHRONOS', 'ONECHRONOS MARKETS LLC'],
          status: FinraBrokerCheckBrokerStatus.Active,
          'office-address': {
            'street-1': '433 BROADWAY',
            'street-2': 'STE 211',
            city: 'NEW YORK',
            subdivision: 'NY',
            'postal-code': '10013',
          },
          'mailing-address': {
            'street-1': '433 BROADWAY',
            'street-2': 'STE 211',
            city: 'NEW YORK',
            subdivision: 'NY',
            'postal-code': '10013',
          },
          'phone-number': '917-300-0108',
          'broker-dealer-sec-number': '69882',
          'broker-dealer-full-sec-number': '8-69882',
          'has-disclosure-filings': false,
          'approved-finra-registration-count': 1,
          'branches-count': 2,
        },
      ],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_124',
    type: ApiModel.ReportFinraBrokerCheck,
  };
}

export function createPreviewReportPhoneNumber(
  attributes?: Partial<ReportPhoneNumberAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportPhoneNumber {
  return {
    attributes: {
      'primary-input': '15005550006',
      status: ReportStatus.Ready,
      'created-at': createdAt,
      'completed-at': completedAt,
      'phone-number': '15005550006',
      'phone-type': 'MOBILE',
      'phone-carrier': 'Verizon Wireless',
      'risk-level': 'low',
      'risk-recommendation': 'allow',
      'risk-score': 11,
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_125',
    type: ApiModel.ReportPhoneNumber,
  };
}

export function createPreviewReportPoliticallyExposedPersonUpdated(
  attributes?: Partial<ReportPoliticallyExposedPersonAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportPoliticallyExposedPerson {
  return {
    attributes: {
      'primary-input': 'Michelle Obama',
      status: ReportStatus.Ready,
      'created-at': createdAt,
      'completed-at': completedAt,
      query: {
        term: 'Michelle Obama',
        'name-first': 'Michelle',
        'name-middle': undefined,
        'name-last': 'Obama',
        birthdate: undefined,
      },
      result: [
        {
          'entity-id': 'Q13133',
          name: 'Michelle Obama',
          aliases: [
            'Michelle Obamová',
            'Mişel Obama',
            'Mišela Obama',
            'Μισέλ Ομπάμα',
            'Мишел Обама',
            'Мишель Обама',
            'Мішель Обама',
            'Мішэль Абама',
            'Միշել Օբամա',
            'מישל אובמה',
            'مشیل اوباما',
            'ميشيل أوباما',
            'میشل اوباما',
            'मिशेल ओबामा',
            'मिसेल ओबामा',
            'ಮಿಚೆಲ್ ಒಬಾಮ',
            'မီရှဲလ် အိုဘားမား',
            'მიშელ ობამა',
            'ミシェル・オバマ',
            '米歇爾·奧巴馬',
            '미셸 오바마',
            'First Lady Michelle Obama',
            'Michael LaVaughn Robinson Obama',
            'Michelle LaVaughn Obama',
            'Michelle LaVaughn Obama nee Robinson',
            'Michelle LaVaughn Obama née Robins',
            'Michelle LaVaughn Obama née Robinson',
            'Michelle LaVaughn Robinson',
            'Michelle LaVaughn Robinson Obama',
            'Michelle Robinson',
            'Michelle daVaughn Robinson Obama',
            'Prima Doamnă Michelle Obama',
            'Обама Мішель',
            'Обама, Мишель',
          ],
          birthdates: ['1964-01-17'],
          topics: [],
          'death-dates': [],
          locations: ['US'],
          country: 'US',
          sex: 'female',
          candidacies: [],
          positions: [
            {
              'pep-class': 'pep-class-1',
              'source-key': 'internal-data-source',
              name: 'First Lady of the United States',
              'start-date': '2009-01-20',
              'end-date': '2017-01-20',
              'dataset-links': [
                {
                  dataset: 'ann_pep_positions',
                  'display-name': 'Wikidata Politically Exposed Persons',
                  'default-url': 'https://www.wikidata.org/wiki/Q13133',
                  'flag-code': '',
                },
                {
                  dataset: 'wd_peps',
                  'display-name': 'Wikidata Politically Exposed Persons',
                  'default-url': 'https://www.wikidata.org/wiki/Q13133',
                  'flag-code': '',
                },
              ],
              topics: ['National government'],
            },
            {
              'pep-class': 'pep-class-1',
              'source-key': 'internal-data-source',
              name: 'First Lady of the United States',
              'start-date': '2009-01-20',
              'end-date': '2017-01-20',
              'dataset-links': [],
              topics: ['National government'],
            },
          ],
          associates: [
            {
              name: 'Capers Funnye',
              relationship: 'Second Uncle',
              'pep-class': 'pep-class-1',
              topics: [],
            },
            {
              name: 'Marian Robinsonová',
              relationship: 'Mother',
              'pep-class': 'pep-class-1',
              topics: [],
            },
            {
              name: 'Barack Obama',
              relationship: 'Spouse',
              topics: [],
              'pep-class': 'pep-class-1',
            },
            {
              name: 'Malia Obama',
              relationship: 'Child',
              topics: [],
              'pep-class': 'pep-class-1',
            },
            {
              name: 'Natasha Obama',
              relationship: 'Child',
              topics: [],
              'pep-class': 'pep-class-1',
            },
            {
              name: 'Craig Robinson',
              relationship: 'Elder Brother',
              topics: [],
              'pep-class': 'pep-class-1',
            },
          ],
          'related-urls': [],
          images: [
            {
              'internal-url':
                'https://commons.wikimedia.org/w/index.php?title=Special:Redirect/file/Michelle_Obama_official_portrait_headshot.jpg',
              'external-url':
                'https://commons.wikimedia.org/w/index.php?title=Special:Redirect/file/Michelle_Obama_official_portrait_headshot.jpg',
              'similarity-level': 'low',
            },
          ],
          affiliations: [],
          sources: {
            'internal-data-source': {
              'flag-code': '',
              name: 'Internal data source',
              url: 'https://www.obama.org/',
            },
            'wikidata-politically-exposed-persons': {
              'flag-code': '',
              name: 'Wikidata Politically Exposed Persons',
              url: 'https://www.wikidata.org/wiki/Q13133',
            },
            wikidata: {
              'flag-code': '',
              name: 'Wikidata',
              url: 'https://www.wikidata.org/wiki/Wikidata:Main_Page',
            },
          },
          'match-types': ['name_exact'],
          'pep-type': 'politically_exposed_person',
          'data-source': 'persona',
        },
      ],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_126',
    type: ApiModel.ReportPoliticallyExposedPerson,
  };
}

export function createPreviewReportPoliticallyExposedPerson(
  attributes?: Partial<ReportPoliticallyExposedPersonAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportPoliticallyExposedPerson {
  return {
    attributes: {
      'primary-input': 'Boris Johnson',
      status: ReportStatus.Ready,
      'created-at': createdAt,
      'completed-at': completedAt,
      query: {
        term: 'Boris Johnson',
      },
      result: [
        {
          name: 'Boris Johnson',
          birthdates: ['1970-01-01'],
          'death-dates': [],
          country: 'UK',
          positions: [
            {
              'source-key': 'pep-uk-parliament-mps',
              name: 'Member Of Parliament For The United Kingdom',
              'pep-class': 'pep-class-1',
              'start-date': '2023-01-01',
              'end-date': '2024-01-01',
              'dataset-links': [],
              topics: [],
            },
            {
              'source-key': 'pep-uk-parliament',
              name: 'Member Of The National Parliament',
              'pep-class': 'pep-class-1',
              'start-date': '2023-01-01',
              'end-date': '2024-01-01',
              'dataset-links': [],
              topics: [],
            },
            {
              'source-key': 'us-cia-world-leaders',
              name: 'Prime Minister, First Lord Of The Treasury, Minister For The Union, & Minister For The Civil Service',
              'pep-class': 'pep-class-1',
              'start-date': '2023-01-01',
              'end-date': '2024-01-01',
              'dataset-links': [],
              topics: [],
            },
            {
              'source-key': 'internal-data-source',
              name: 'Member Of The Privy Council Of The United Kingdom',
              'pep-class': 'pep-class-1',
              'start-date': '2023-01-01',
              'end-date': '2024-01-01',
              'dataset-links': [],
              topics: [],
            },
          ],
          'related-urls': [],
          images: [],
          affiliations: [],
          sources: {
            'pep-uk-parliament-mps': {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              url: null,
              name: 'PEP UK MPs',
              'flag-code': 'GB',
            },
            'pep-uk-parliament': {
              url: 'http://www.parliament.uk/mps-lords-and-offices/',
              name: 'United Kingdom Parliament',
              'flag-code': 'GB',
            },
            'us-cia-world-leaders': {
              url: 'https://www.cia.gov/library/publications/world-leaders-1/',
              name: 'Central Intelligence Agency Foreign Governments Leadership',
              'flag-code': 'US',
            },
            'internal-data-source': {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              url: null,
              name: 'Internal data source',
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              'flag-code': null,
            },
          },
          'match-types': ['name_exact'],
        },
      ],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_126',
    type: ApiModel.ReportPoliticallyExposedPerson,
  };
}

export function createPreviewReportProfile(
  attributes?: Partial<ReportProfileAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportProfile {
  return {
    attributes: {
      'primary-input': 'Alexander Sample',
      status: ReportStatus.Ready,
      'created-at': createdAt,
      'completed-at': completedAt,
      'address-city': 'San Francisco',
      'address-postal-code': '065100000',
      'address-street-1': '600 California St',
      'address-street-2': '11F',
      'address-subdivision': 'California',
      birthdate: '1987-10-31',
      'country-code': 'US',
      'name-first': 'Alexander',
      'name-last': 'Sample',
      'social-security-number': '123456789',
      'social-security-number-last-4': '6789',
      'identity-records': [
        {
          names: [
            {
              'name-full': 'Alexander Jack Sample',
              'name-first': 'Alexander',
              'name-middle': 'Jack',
              'name-last': 'Sample',
            },
          ],
          birthdates: ['2018-10-31'],
          addresses: [
            {
              'country-code': 'US',
              city: 'San Francisco',
              'postal-code': '065100000',
              'street-1': '600 California St',
              'street-2': '11F',
              subdivision: 'California',
            },
          ],
          'social-security-numbers': ['123456789'],
          'phone-numbers': [],
          'email-addresses': ['alexander@sample.com'],
          deceased: false,
        },
      ],
      'data-format': 'legacy',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'use-case': null,
      'report-template-version-name': 'v1',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_127',
    type: ApiModel.ReportProfile,
  };
}

export function createPreviewReportProfileNonAuthoritative(
  attributes?: Partial<ReportProfileNonAuthoritativeAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportProfileNonAuthoritative {
  return {
    attributes: {
      'primary-input': 'sample@alexander.com',
      'completed-at': completedAt,
      'created-at': createdAt,
      status: ReportStatus.Ready,
      query: {
        'email-address': 'sample@alexander.com',
      },
      result: {
        name: 'Alexander Sample',
        birthdate: '1987-10-31',
        emails: [
          {
            address: 'sample@alexander.com',
            type: 'current_professional',
          },
          {
            address: 'alexander@sample.com',
            type: 'professional',
          },
        ],
        'phone-numbers': ['+15005550006'],
        location: {
          locality: 'San Francisco',
          region: 'California',
          country: 'United States',
        },
        education: [
          {
            'start-date': '2017',
            'end-date': '2021',
            'school-name': 'university of california',
            'school-type': 'college',
          },
        ],
        experience: [
          {
            'end-date': '2021',
            'start-date': '2021',
            'company-name': 'persona',
            'title-name': 'engineer',
          },
        ],
        'social-media-profiles': [
          {
            network: 'linkedin',
            url: 'linkedin.com/',
            username: 'linkedin',
          },
          {
            network: 'facebook',
            url: 'facebook.com/',
            username: 'facebook',
          },
          {
            network: 'twitter',
            url: 'twitter.com/',
            username: 'twitter',
          },
          {
            network: 'github',
            url: 'github.com/',
            username: 'github',
          },
        ],
      },
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_001',
    type: ApiModel.ReportProfileNonAuthoritative,
  };
}

export function createPreviewReportSynthetic(
  attributes?: Partial<ReportSyntheticAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportSynthetic {
  return {
    attributes: {
      'primary-input': 'Alexander Sample',
      status: ReportStatus.Ready,
      'address-city': 'San Francisco',
      'address-postal-code': '065100000',
      'address-street-1': '600 California St',
      'address-street-2': '11F',
      'address-subdivision': 'California',
      birthdate: '1987-10-31',
      'completed-at': completedAt,
      'country-code': 'US',
      'created-at': createdAt,
      'name-first': 'Alexander',
      'name-last': 'Sample',
      'social-security-number': '123456789',
      'sentilink-abuse-score': 111,
      'sentilink-first-party-synthetic-score': 444,
      'sentilink-third-party-synthetic-score': 555,
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_128',
    type: ApiModel.ReportSynthetic,
  };
}

export function createPreviewReportAddressLookup(
  attributes?: Partial<ReportAddressLookupAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportAddressLookup {
  return {
    attributes: {
      'primary-input': '600 CALIFORNIA STREET',
      status: ReportStatus.Ready,
      'address-city': 'San Francisco',
      'address-postal-code': '94109',
      'address-street-1': '600 CALIFORNIA STREET',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'address-street-2': null,
      'address-subdivision': 'California',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      addressee: null,
      'analysis-active': 'Y',
      'analysis-dpv-cmra': 'N',
      'analysis-dpv-footnotes': ['AA', 'BB'],
      'analysis-dpv-footnotes-decoded': [
        'Street name, city, state, and ZIP are all valid.',
        'Entire address is valid',
      ],
      'analysis-dpv-match-code': 'Y',
      'analysis-dpv-match-code-decoded': 'Confirmed',
      'analysis-dpv-vacant': 'N',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'metadata-ews-match': null,
      'analysis-footnotes': ['N#'],
      'analysis-footnotes-decoded': ['Fixed abbreviations'],
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'analysis-lacs-link-code': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'analysis-lacs-link-code-decoded': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'analysis-lacs-link-indicator': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'analysis-lacs-link-indicator-decoded': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'analysis-suite-link-match': null,
      'completed-at': completedAt,
      'created-at': createdAt,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'metadata-building-default-indicator': null,
      'metadata-carrier-route': 'C031',
      'metadata-carrier-route-type': 'CityRoute',
      'metadata-congressional-district': '12',
      'metadata-county-fips': '06075',
      'metadata-county-name': 'San Francisco',
      'metadata-latitude': 37.78138,
      'metadata-longitude': -122.39591,
      'metadata-obeys-daylight-savings': true,
      'metadata-precision': 'Zip9',
      'metadata-record-type': 'H',
      'metadata-residential-delivery-indicator': 'Residential',
      'metadata-time-zone': 'Pacific',
      'metadata-utc-offset': -8,
      'metadata-zip-type': 'Standard',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'next-scheduled-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'redacted-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'reference-id': null,
      'resolved-address-city': 'San Francisco',
      'resolved-address-postal-code': '94109-1295',
      'resolved-address-street-1': '600 California St',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'resolved-address-street-2': null,
      'resolved-address-subdivision': 'CA',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'resolved-addressee': null,
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_120',
    type: ApiModel.ReportAddressLookup,
  };
}

export function createPreviewReportAdverseMedia(
  attributes?: Partial<ReportAdverseMediaAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportAdverseMedia {
  return {
    attributes: {
      'primary-input': 'Alexander Sample',
      'completed-at': completedAt,
      'created-at': createdAt,
      status: ReportStatus.Ready,
      'name-first': 'Alexander',
      'name-last': 'Sample',
      term: 'Alexander Sample',
      'related-sources': [
        {
          akas: ['Alexander Sample', 'Alexander J. Sample', 'Alex Sample'],
          'match-types': ['name_exact'],
          sources: [
            {
              'country-codes': ['US'],
              types: ['adverse-media-general'],
            },
          ],
          media: [
            {
              title: 'Riveting article title',
              url: 'https://www.fakenews.com/',
            },
          ],
          birthdates: ['1987-10-31'],
          name: 'Alexander Sample',
        },
      ] as RelatedSource[],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_131',
    type: ApiModel.ReportAdverseMedia,
  };
}

export function createPreviewReportSocialMedia(
  attributes?: Partial<ReportSocialMediaAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportSocialMedia {
  return {
    attributes: {
      'primary-input': 'sample@alexander.com',
      'completed-at': completedAt,
      'created-at': createdAt,
      status: ReportStatus.Ready,
      'twitter-url': 'twitter.com/sample',
      'twitter-username': 'sample',
      'facebook-username': 'sample',
      'facebook-url': 'facebook.com/sample',
      'facebook-id': 'abcdefghijklmn',
      'linkedin-username': 'sample',
      'linkedin-url': 'linkedin.com/sample',
      'linkedin-id': 'sample',
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_001',
    type: ApiModel.ReportSocialMedia,
  };
}

export function createPreviewReportWatchlistUpdated(
  attributes?: Partial<ReportWatchlistAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportWatchlist {
  return {
    attributes: {
      'primary-input': 'Terry Lawson',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      'redacted-at': undefined,
      'name-first': 'Terry',
      'name-middle': undefined,
      'name-last': 'Lawson',
      term: 'Terry Lawson',
      birthdate: '1987-10-31',
      'country-code': 'US',
      'matched-lists': [
        {
          name: 'US - OFAC - Specially Designated Nationals and Blocked Persons (SDN)',
          url: 'http://bit.ly/1MLgpye',
          'list-type': 'sanction' as WatchlistType,
          slug: 'us-ofac-specially-designated-nationals-and-blocked-persons',
          'flag-code': 'US',
          entities: [
            {
              name: 'Terry Lawson',
              'alias-type': 'primary_name',
              birthdates: [
                {
                  granularity: 'day',
                  day: '31',
                  month: '10',
                  year: '1987',
                },
              ],
              'group-id': 'US:17386',
            },
          ],
        },
      ],
      'entity-lists': [
        {
          id: 'US:17386',
          names: ['Terry Lawson'],
          aliases: ['Terry', 'Scary Terry'],
          birthdates: [
            {
              granularity: 'day',
              year: '1987',
              month: '10',
              day: '31',
            },
          ],
          locations: [
            {
              type: 'nationality',
              country: 'US',
            },
            {
              type: 'birthplace',
              country: 'CA',
            },
          ],
          identifications: [],
          watchlists: [
            {
              name: 'US - OFAC - Specially Designated Nationals and Blocked Persons (SDN)',
              slug: 'us-ofac-specially-designated-nationals-and-blocked-persons',
              tags: ['ofac', 'us'],
              'list-type': 'sanction' as WatchlistType,
              url: 'http://bit.ly/1MLgpye',
              'flag-code': 'US',
              entities: [
                {
                  name: 'Terry Lawson',
                  'alias-type': 'primary_name',
                  birthdates: [
                    {
                      granularity: 'day',
                      year: '1987',
                      month: '10',
                      day: '31',
                    },
                  ],
                  'group-id': 'US:17386',
                },
              ],
            },
          ],
          'ignore-list': [],
          details: [],
        },
      ],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_132',
    type: ApiModel.ReportWatchlist,
  };
}

export function createPreviewReportWatchlist(
  attributes?: Partial<ReportWatchlistAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportWatchlist {
  return {
    attributes: {
      'primary-input': 'Terry Lawson',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      'matched-lists': [
        {
          name: 'UN - Security Council - Al-Qaida',
          url: 'https://scsanctions.un.org/consolidated/',
          'list-type': 'sanction' as WatchlistType,
          slug: 'un-sc-al-quaida',
          'flag-code': 'UN',
          entities: [
            {
              name: 'Terry Lawson',
              'alias-type': 'primary_name',
              birthdates: [
                {
                  granularity: 'day',
                  day: '1',
                  month: '2',
                  year: '1970',
                },
              ],
            },
            {
              name: 'Terry Lawson',
              'alias-type': 'aka',
              birthdates: [
                {
                  granularity: 'circa_year',
                  day: '1',
                  month: '1',
                  year: '1970',
                },
                {
                  granularity: 'circa_year',
                  day: '1',
                  month: '1',
                  year: '1980',
                },
                {
                  granularity: 'month',
                  day: '1',
                  month: '1',
                  year: '1970',
                },
              ],
            },
          ],
        },
      ],
      'searched-categories': [
        WatchlistType.Pep,
        WatchlistType.Sanction,
        WatchlistType.FitnessProbity,
      ],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_132',
    type: ApiModel.ReportWatchlist,
  };
}

export function createPreviewReportSecActionLookup(
  attributes?: Partial<ReportSecActionLookupAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportSecActionLookup {
  return {
    attributes: {
      'primary-input': 'STEVEN A COHEN',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      'name-first': 'Steven',
      'name-last': 'Cohen',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      birthdate: null,
      result: [
        {
          'name-full': 'STEVEN A COHEN',
          'age-range': [62, 64],
          'address-subdivision': 'New York',
          'enforcement-action':
            'SECURITIES AND EXCHANGE COMMISSION v. GLENN COHEN, CRAIG COHEN, MARC I. COHEN, STEVEN A. COHEN, and LAURIE G. TOPAL, 1:14-cv-03655 (S.D.N.Y.)',
          'enforcement-action-date': '2014-05-22',
          'releases-and-documents': [
            {
              title: 'SEC Complaint',
              url: 'https://www.sec.gov/litigation/complaints/2014/comp-pr2014-104.pdf',
              date: '2014-05-22',
            },
            {
              title: 'Judgment',
              url: 'https://www.sec.gov/files/Judg14-cv-03655CohenSteven.pdf',
              date: '2014-05-27',
            },
            {
              title: 'LR-22999',
              url: 'https://www.sec.gov/litigation/litreleases/2014/lr22999.htm',
              date: '2014-05-22',
            },
          ],
        },
      ],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_133',
    type: ApiModel.ReportSecActionLookup,
  };
}

export function createPreviewReportBusinessAdverseMedia(
  attributes?: Partial<ReportBusinessAdverseMediaAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessAdverseMedia {
  return {
    attributes: {
      'primary-input': 'Alexander Sample',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      term: 'Alexander Sample',
      'country-code': 'US',
      'related-sources': [
        {
          akas: ['Alexander Sample', 'Alexander J. Sample', 'Alex Sample'],
          'match-types': ['name_exact'],
          sources: [
            {
              'country-codes': ['US'],
              types: ['adverse-media-general'],
            },
          ],
          media: [
            {
              title: 'Riveting article title',
              url: 'https://www.fakenews.com/',
            },
          ],
          birthdates: ['1987-10-31'],
          name: 'Alexander Sample',
        },
      ] as RelatedSource[],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_134',
    type: ApiModel.ReportBusinessAdverseMedia,
  };
}

export function createPreviewReportBusinessAssociatedPersons(
  attributes?: Partial<ReportBusinessAssociatedPersonsAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessAssociatedPersons {
  return {
    attributes: {
      'primary-input': 'Alexander Sample',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      metadata: {
        'database-business-token': 'ver_12345',
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_134',
    type: ApiModel.ReportBusinessAssociatedPersons,
  };
}

export function createPreviewReportBusinessClassification(
  attributes?: Partial<ReportBusinessClassificationAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessClassification {
  return {
    attributes: {
      'primary-input': 'Globovision',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      query: {
        'business-name': 'Globovision',
        website: 'https://www.globovision.com',
      },
      result: {
        'naics-information': [
          {
            analysis:
              'Globovisión operates a comprehensive news website that covers a wide range of topics including national and international news, economy, sports, entertainment, health, technology, tourism, gastronomy, lifestyle, and curiosities. This aligns with the activities described under NAICS code 519130, which includes establishments primarily engaged in publishing and/or broadcasting content on the Internet.',
            code: '519130',
            confidence: 'high',
            description: 'Internet Publishing and Broadcasting and Web Search Portals',
          },
          {
            analysis:
              'Although the primary focus of Globovisión is its news website, the company is a media entity that may also engage in television broadcasting activities, especially given its comprehensive coverage of various topics and special programs. This aligns with NAICS code 515120, which includes establishments primarily engaged in broadcasting television programs.',
            code: '515120',
            confidence: 'medium',
            description: 'Television Broadcasting',
          },
        ],
        'mcc-information': [
          {
            analysis:
              'Globovisión Tele C.A. is primarily a television network and media company that provides a wide range of news and entertainment content. The core of their business involves broadcasting and telecommunication services, which aligns closely with MCC 4833. This code is typically used for businesses involved in telecommunication services, including television broadcasting.',
            code: '4833',
            confidence: 'high',
            description:
              'Telecommunication Services including Local and Long Distance Calls, Credit Card Calls, Fax Services, and more.',
          },
          {
            analysis:
              'Given that Globovisión is involved in producing and broadcasting television programs, another relevant MCC could be 4899. This code is used for businesses that provide cable, satellite, and other pay television and radio services. While Globovisión is not explicitly described as a pay television service, the nature of their broadcasting activities makes this MCC a relevant consideration.',
            code: '4899',
            confidence: 'medium',
            description: 'Cable, Satellite, and Other Pay Television and Radio Services.',
          },
        ],
        keywords: [
          'news',
          'broadcasting',
          'entertainment',
          'sports',
          'technology',
          'health',
          'lifestyle',
          'economy',
          'current events',
          'journalism',
        ],
        'is-high-risk': false,
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_134',
    type: ApiModel.ReportBusinessClassification,
  };
}

export function createPreviewReportBusinessIndustryClassification(
  attributes?: Partial<ReportBusinessIndustryClassificationAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessIndustryClassification {
  return {
    attributes: {
      'primary-input': 'Globovision',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      query: {
        'business-name': 'Globovision',
        website: 'globovision.com',
      },
      result: {
        id: 'c745ae46-ca20-47ad-bfbd-2ee6ec49e619',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        name: null,
        status: 'in_review',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'industry-classification': {
          object: null,
          id: 'd52dc708-8396-4c28-825b-80bf53f4eda6',
          status: 'completed',
          categories: [
            {
              name: 'Other Non-Prohibited',
              sector: null,
              category: 'OTHER_NON_PROHIBITED',
              score: 0.7550519504607509,
              'high-risk': false,
              'naics-codes': [],
              'sics-codes': [],
            },
            {
              name: 'Professional, Scientific or Technical Services',
              sector: 'PROFESSIONAL_SCIENTIFIC_TECHNICAL_SERVICES',
              category: 'PROFESSIONAL_SERVICES',
              score: 0.6093762516975403,
              'high-risk': false,
              'naics-codes': ['54'],
              'sics-codes': [],
            },
          ],
        },
        'created-at': '2023-08-10T20:05:53.409Z',
        'updated-at': '2023-08-10T20:06:14.037Z',
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_134',
    type: ApiModel.ReportBusinessIndustryClassification,
  };
}

export function createPreviewReportBusinessLiensLookup(
  attributes?: Partial<ReportBusinessLiensLookupAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessLiensLookup {
  return {
    attributes: {
      'primary-input': 'TECH INNOVATIONS, LLC',
      status: ReportStatus.Ready,
      'created-at': '2024-04-24T18:54:11.000Z',
      'completed-at': '2024-04-24T18:57:07.000Z',
      'redacted-at': undefined,
      'parent-marked-for-cascading-redaction-at': undefined,
      'next-scheduled-at': undefined,
      'creator-token': 'user_abc',
      'creator-type': 'User',
      'creator-name': 'Taylor Swift',
      'report-template-version-name': 'v1',
      'report-template-version-interval-seconds': undefined,
      'reference-id': undefined,
      'has-match': true,
      'business-name': 'TECH INNOVATIONS, LLC',
      query: {
        'address-country-code': 'US',
      },
      result: {
        'ucc-filings': [
          {
            'filing-date': '2015-03-22',
            'expiration-date': '2020-03-22',
            'verified-date': '2015-06-10',
            status: FilingStatus.Closed,
            'document-type': 'Original',
            'filing-reference': '0003751892',
            'original-filing-date': undefined,
            'original-filing-reference': '0003751892',
            'filing-office': {
              name: 'STATE DEPARTMENT OF ASSESSMENTS AND TAXATION',
              duns: '789456123',
              'street-address-1': '301 W PRESTON ST',
              'street-address-2': 'ROOM 801',
              'postal-code': '21201',
              'address-region': 'MD',
              'address-city': 'BALTIMORE',
              'address-country': 'US',
            },
            'secured-parties': [
              {
                name: 'GLOBAL FINANCIAL SERVICES, INC.',
                duns: undefined,
                'street-address-1': '500 MAIN ST, SUITE 300',
                'street-address-2': undefined,
                'postal-code': '10001',
                'address-region': 'NY',
                'address-city': 'NEW YORK',
                'address-country': 'US',
              },
            ],
            debtors: [
              {
                name: 'TECH INNOVATIONS, LLC',
                duns: '987654321',
                'street-address-1': '123 INNOVATION WAY',
                'street-address-2': undefined,
                'postal-code': '94105',
                'address-region': 'CA',
                'address-city': 'SAN FRANCISCO',
                'address-country': 'US',
              },
            ],
            collaterals: [
              {
                'collateral-class': 'Intellectual Property',
                'collateral-type': 'Patents',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
              {
                'collateral-class': 'Equipment',
                'collateral-type': 'Computer Hardware',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
            ],
          },
          {
            'filing-date': '2018-09-05',
            'expiration-date': '2023-09-05',
            'verified-date': '2018-11-20',
            status: FilingStatus.Open,
            'document-type': 'Amendment',
            'filing-reference': '0004567890',
            'original-filing-date': undefined,
            'original-filing-reference': '0001257890',
            'filing-office': {
              name: 'SECRETARY OF STATE',
              duns: '123789456',
              'street-address-1': '1500 11TH STREET',
              'street-address-2': undefined,
              'postal-code': '95814',
              'address-region': 'CA',
              'address-city': 'SACRAMENTO',
              'address-country': 'US',
            },
            'secured-parties': [
              {
                name: 'WESTCOAST CAPITAL GROUP',
                duns: undefined,
                'street-address-1': '888 FINANCIAL PLAZA',
                'street-address-2': 'FLOOR 20',
                'postal-code': '90017',
                'address-region': 'CA',
                'address-city': 'LOS ANGELES',
                'address-country': 'US',
              },
            ],
            debtors: [
              {
                name: 'TECH INNOVATIONS, LLC',
                duns: '987654321',
                'street-address-1': '123 INNOVATION WAY',
                'street-address-2': undefined,
                'postal-code': '94105',
                'address-region': 'CA',
                'address-city': 'SAN FRANCISCO',
                'address-country': 'US',
              },
            ],
            collaterals: [
              {
                'collateral-class': 'industrial equipment',
                'collateral-type': 'Manufacturing Equipment',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
              {
                'collateral-class': 'inventories',
                'collateral-type': 'Raw Materials',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
              {
                'collateral-class': 'Book Debts',
                'collateral-type': 'Accounts Receivables',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
            ],
          },
          {
            'filing-date': '2019-11-30',
            'expiration-date': '2024-11-30',
            'verified-date': '2020-01-15',
            status: FilingStatus.Open,
            'document-type': 'Continuation',
            'filing-reference': '0005678901',
            'original-filing-date': undefined,
            'original-filing-reference': '0005678901',
            'filing-office': {
              name: 'DEPARTMENT OF STATE',
              duns: '987321654',
              'street-address-1': '99 WASHINGTON AVE',
              'street-address-2': undefined,
              'postal-code': '12231',
              'address-region': 'NY',
              'address-city': 'ALBANY',
              'address-country': 'US',
            },
            'secured-parties': [
              {
                name: 'EASTERN BANK & TRUST',
                duns: undefined,
                'street-address-1': '200 WALL STREET',
                'street-address-2': 'SUITE 500',
                'postal-code': '10005',
                'address-region': 'NY',
                'address-city': 'NEW YORK',
                'address-country': 'US',
              },
            ],
            debtors: [
              {
                name: 'TECH INNOVATIONS, LLC',
                duns: '987654321',
                'street-address-1': '123 INNOVATION WAY',
                'street-address-2': undefined,
                'postal-code': '94105',
                'address-region': 'CA',
                'address-city': 'SAN FRANCISCO',
                'address-country': 'US',
              },
            ],
            collaterals: [
              {
                'collateral-class': 'Equipment',
                'collateral-type': 'Point of Sale Systems',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
              {
                'collateral-class': 'inventories',
                'collateral-type': 'Merchandise',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
              {
                'collateral-class': 'Fixtures',
                'collateral-type': 'Store Fixtures',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
              {
                'collateral-class': 'Book Debts',
                'collateral-type': 'Credit Card Receivables',
                'collateral-supplemental-items': ['and proceeds'],
                'collateral-scope-type': undefined,
              },
            ],
          },
        ],
      },
      metadata: {
        'database-business-token': 'ver_123',
      },
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_251',
    type: ApiModel.ReportBusinessLiensLookup,
  };
}

export function createPreviewReportBusinessLookup(
  attributes?: Partial<ReportBusinessLookupAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessLookup {
  return {
    attributes: {
      'primary-input': 'Globovision',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      'match-status-address-city': MatchStatus.Match,
      'match-status-address-postal-code': MatchStatus.Match,
      'match-status-address-street': MatchStatus.NoMatch,
      'match-status-associated-person': MatchStatus.NotApplicable,
      'match-status-business-name': MatchStatus.Match,
      'match-status-ein': MatchStatus.Match,
      'match-status-phone-number': MatchStatus.Unknown,
      query: {
        'business-name': 'Globovision Tele CA, Corp',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'address-street-1': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'address-street-2': null,
        'address-city': 'Miami',
        'address-subdivision': 'FL',
        'address-postal-code': '33146-1747',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'phone-number': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        website: null,
        'associated-people': [],
      },
      result: {
        name: 'Globovision',
        addresses: [
          {
            city: 'Miami',
            'delivery-point-barcode': '331461747040',
            'postal-code': '33146-1747',
            'primary-number': '4100',
            'street-1': '4100 Salzedo St Apt 804',
            'street-2': null,
            subdivision: 'FL',
          },
          {
            city: 'Miami',
            'delivery-point-barcode': '331741222465',
            'postal-code': '33174-1222',
            'primary-number': '11046',
            'street-1': '11046 W Flagler St',
            'street-2': null,
            subdivision: 'FL',
          },
        ],
        'match-confidence': 0.23,
        aliases: ['GLOBOVISION TELE CA CORP'],
        identifiers: [
          {
            'file-number': '0000000',
            type: BusinessIdentifierType.Ein,
          },
          {
            'file-number': 'P16000012017',
            'issue-date': '2016-02-04',
            state: 'FL',
            type: BusinessIdentifierType.UsStateRegistrationNumber,
          },
          {
            'file-number': '2016-02-04',
            'issue-date': '2013-07-02',
            state: 'FL',
            type: BusinessIdentifierType.UsStateRegistrationNumber,
          },
        ],
        officers: [
          {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'name-first': null,
            'name-full': 'JESSE PA DEAN-KLUGER',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'name-last': null,
            titles: [],
          },
        ],
        agents: [
          {
            'name-full': "MARTORELL'S OFFICE GROUP CORP",
          },
        ],
        'phone-numbers': [],
        websites: [],
        'legal-entity-type': 'CORPORATION',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        headcount: null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        description: null,
        'date-of-incorporation': {
          year: '2016',
        },
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_135',
    type: ApiModel.ReportBusinessLookup,
  };
}

export function createPreviewReportBusinessNonprofit(
  attributes?: Partial<ReportBusinessNonprofitAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessNonprofit {
  return {
    attributes: {
      'primary-input': '12-3456789',
      status: ReportStatus.Ready,
      'created-at': '2024-04-24T18:54:11.000Z',
      'completed-at': '2024-04-24T18:57:07.000Z',
      'redacted-at': undefined,
      'parent-marked-for-cascading-redaction-at': undefined,
      'next-scheduled-at': undefined,
      'creator-token': 'user_FbetgKRJqxGmbFuwj9sXZuPnMoe2',
      'creator-type': 'User',
      'creator-name': 'Taylor Swift',
      'report-template-version-name': 'v1',
      'report-template-version-interval-seconds': undefined,
      'reference-id': undefined,
      'has-match': true,
      query: { ein: '12-3456789' },
      result: {
        'organization-name': 'Candid',
        'organization-name-aka':
          'The Foundation Center,  Foundation Center, GuideStar, GuideStar USA',
        address: {
          'street-1': '32 Old Slip',
          'street-2': '24th Floor',
          city: 'New York',
          subdivision: 'NY',
          'postal-code': '10005',
          'country-code': 'US',
        },
        'foundation-type-code': 'PC',
        'foundation-type-description': 'Public charity described in section 509(a)(1) or (2)',
        'irs-pub-78-details': {
          'church-message': undefined,
          'organization-name': 'Candid',
          ein: '12-3456789',
          verified: true,
          city: 'New York',
          state: 'NY',
          'organization-types': [
            {
              'organization-type': 'A public charity (50% deductibility limitation).',
              'deductibility-limitation': '50%',
              'deductibility-status-description': 'PC',
            },
          ],
          'most-recent-pub78': '4/8/2024 12:00:00 AM',
          'most-recent-irb': '4/22/2024 12:00:00 AM',
        },
        'irs-bmf-details': {
          'church-message': undefined,
          'organization-name': 'CANDID',
          ein: '12-3456789',
          status: true,
          'most-recent-bmf': '4/8/2024 12:00:00 AM',
          subsection: '03',
          'subsection-description': '501(c)(3) Public Charity',
          'ruling-month': '06',
          'ruling-year': '1957',
        },
        'ca-ag-details': {
          status: CaliforniaAttorneyGeneralStatus.MayOperate,
          'registry-status': 'Current',
          'state-charity-reg-num': '021534',
          'issue-date': '12/31/2005 12:00:00 AM',
          'last-renewal-date': '8/2/2023 12:00:00 AM',
          'status-set-date': '8/2/2023 12:00:00 AM',
          'as-of-date': '8/16/2023 12:00:00 AM',
          'most-recent-ag': '4/4/2024 12:00:00 AM',
        },
        'ca-ftb-details': {
          'revocation-status': false,
          'revocation-status-date': '',
          'most-recent-ftb': '',
        },
      },
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_456',
    type: ApiModel.ReportBusinessNonprofit,
  };
}

export function createPreviewReportBusinessOnlinePresence(
  attributes?: Partial<ReportBusinessOnlinePresenceAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessOnlinePresence {
  return {
    attributes: {
      'primary-input': 'Persona Identities',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      query: {
        'business-name': 'Persona Identities',
        'address-city': 'San Francisco',
        'address-subdivision': 'CA',
        'address-country-code': 'US',
      },
      result: {
        homepage: 'https://withpersona.com',
      },
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_001',
    type: ApiModel.ReportBusinessOnlinePresence,
  };
}

export function createPreviewReportBusinessRegistrationsLookup(
  attributes?: Partial<ReportBusinessRegistrationsLookupAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessRegistrationsLookup {
  return {
    attributes: {
      'primary-input': 'ACME, INC.',
      status: ReportStatus.Ready,
      'created-at': '2023-06-01T01:00:00.000Z',
      'completed-at': '2023-06-01T01:00:00.000Z',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'redacted-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'next-scheduled-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'creator-token': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'creator-type': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'creator-name': null,
      'report-template-version-name': 'v1',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template-version-interval-seconds': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'reference-id': null,
      'has-match': true,
      query: {
        'address-country-code': 'US',
      },
      'business-name': 'ACME, INC.',
      'business-formation-country': 'US',
      'business-formation-subdivision': 'DE',
      'business-formation-date': '2018-07-17',
      'business-entity-type': 'Corporation',
      result: {
        'registry-records': [
          {
            type: 'us_secretary_of_state',
            'issue-date': '2021-07-17',
            'issuing-authority': 'de_secretary_of_state',
            'file-number': '123',
            'registered-name': 'ACME, INC.',
            'registration-type': 'domestic',
            'registration-state': 'DE',
            status: {
              raw: 'active',
              standardized: 'active',
            },
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'home-jurisdiction-state': null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'legal-entity-type': null,
            addresses: [
              {
                type: 'physical',
                'street-1': '123 Main Street',
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                'street-2': null,
                city: 'NEWARK',
                subdivision: 'DE',
                'postal-code': '11111',
                'country-code': 'US',
              },
            ],
            'associated-persons': [
              {
                names: [
                  {
                    value: 'Jane Sample',
                  },
                ],
                positions: [
                  {
                    value: 'CEO',
                  },
                ],
              },
            ],
            documents: [
              {
                date: '2/6/2024',
                name: 'Publicly Traded Corporate Disclosure Statement',
                url: 'https://notarealsos.gov/file/1',
              },
              {
                date: '11/14/2023',
                name: 'Statement of Information',
                url: 'https://notarealsos.gov/file/2',
              },
              {
                date: '5/22/2023',
                name: 'Filing Office Statement',
                url: 'https://notarealsos.gov/file/3',
              },
            ],
          },
          {
            type: 'us_secretary_of_state',
            'issue-date': '2021-11-09',
            'issuing-authority': 'ca_secretary_of_state',
            'file-number': '456',
            'registered-name': 'ACME, INC.',
            'registration-type': 'foreign',
            'registration-state': 'CA',
            status: {
              raw: 'expired',
              standardized: 'inactive',
            },
            'home-jurisdiction-state': 'DELAWARE',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'legal-entity-type': null,
            addresses: [
              {
                type: 'mailing',
                'street-1': '51 MAIN ST # 95',
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                'street-2': null,
                city: 'SAN FRANCISCO',
                subdivision: 'CA',
                'postal-code': '11111',
                'country-code': 'US',
              },
            ],
            'associated-persons': [],
          },
        ],
      },
      metadata: {
        'database-business-token': 'ver_12345',
      },
      enrichments: [
        {
          'file-number': '605286571',
          'issuing-authority': 'de_secretary_of_state',
          status: 'completed',
        },
        {
          'file-number': '4211921',
          'issuing-authority': 'ca_secretary_of_state',
          status: 'pending',
        },
      ],
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_251',
    type: ApiModel.ReportBusinessRegistrationsLookup,
  };
}

export function createPreviewReportBusinessSocialMedia(
  attributes?: Partial<ReportBusinessSocialMediaAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessSocialMedia {
  return {
    attributes: {
      'primary-input': 'Sample',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      'twitter-url': 'twitter.com/sample',
      'facebook-url': 'facebook.com/sample',
      'linkedin-url': 'linkedin.com/sample',
      'linkedin-id': 'sample',
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_001',
    type: ApiModel.ReportBusinessSocialMedia,
  };
}

export function createPreviewReportBusinessWatchlist(
  attributes?: Partial<ReportBusinessWatchlistAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessWatchlist {
  return {
    attributes: {
      'primary-input': 'Globovision',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      term: 'Globovision',
      'country-code': 'US',
      'matched-lists': [
        {
          name: 'OFAC SDN List',
          url: 'http://www.treasury.gov/resource-center/sanctions/SDN-List/Pages/default.aspx',
          'list-type': 'sanction' as BusinessWatchlistType,
          slug: 'ofac-sdn-list',
          'flag-code': 'US',
          entities: [
            {
              name: 'Globovision Tele CA, Corp',
              'alias-type': 'primary_name',
            },
          ],
        },
      ],
      'searched-categories': [BusinessWatchlistType.Sanction, BusinessWatchlistType.FitnessProbity],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_000',
    type: ApiModel.ReportBusinessWatchlist,
  };
}

export function createPreviewReportBusinessWebsite(
  attributes?: Partial<ReportBusinessWebsiteAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportBusinessWebsite {
  return {
    attributes: {
      'primary-input': 'Google',
      status: ReportStatus.Ready,
      'created-at': '2023-06-01T01:00:00.000Z',
      'completed-at': '2023-06-01T01:00:00.000Z',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'redacted-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'next-scheduled-at': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'creator-token': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'creator-type': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'creator-name': null,
      'report-template-version-name': 'v1',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template-version-interval-seconds': null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'reference-id': null,
      'has-match': true,
      query: {
        'business-name': 'Google',
        website: 'google.com',
      },
      'business-name': 'Google',
      'match-status-website': BusinessWebsiteMatchStatus.Full,
      result: {
        website: { url: 'google.com' },
        'associated-websites': [
          {
            url: 'google.com',
          },
          {
            url: 'google.com',
          },
        ],
      },
      ...attributes,
    },
    relationships: {
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_123',
    type: ApiModel.ReportBusinessWebsite,
  };
}

export function createPreviewReportChainalysisAddressScreening(
  attributes?: Partial<ReportChainalysisAddressScreening>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportChainalysisAddressScreening {
  return {
    attributes: {
      'primary-input': '0x0038AC785dfB6C82b2c9A7B3B6854e08a10cb9f1',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      query: {
        address: '0x0038AC785dfB6C82b2c9A7B3B6854e08a10cb9f1',
      },
      result: {
        address: '0x0038AC785dfB6C82b2c9A7B3B6854e08a10cb9f1',
        risk: 'Low',
        exposures: [
          {
            category: 'exchange',
            value: 424895.68594,
          },
          {
            category: 'fee',
            value: 1108.16272,
          },
          {
            category: 'unnamed service',
            value: 3612152.81483,
          },
        ],
        status: 'COMPLETE',
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_137',
    type: ApiModel.ReportChainalysisAddressScreening,
  };
}

export function createPreviewReportCryptoAddressWatchlist(
  attributes?: Partial<ReportCryptoAddressWatchlistAttributes>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportCryptoAddressWatchlist {
  return {
    attributes: {
      'primary-input': 'LaizKtS5DUhPuP1nTQcc83MS7HwK6vk85z',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      query: {
        'crypto-address': 'LaizKtS5DUhPuP1nTQcc83MS7HwK6vk85z',
      },
      result: [
        {
          entities: [
            {
              'alias-type': 'primary_name',
              birthdates: [
                {
                  day: '04',
                  granularity: 'day',
                  month: '11',
                  year: '1955',
                },
              ],
              'crypto-addresses': [
                {
                  type: 'XBT',
                  address: '33Kja69SQVc8kozpoP7Qw6HFtGxHkiWzTz',
                },
                {
                  type: 'LTC',
                  address: 'LaizKtS5DUhPuP1nTQcc83MS7HwK6vk85z',
                },
              ],
            },
          ],
          'flag-code': 'US',
          'list-type': 'sanction',
          name: 'US - OFAC - Specially Designated Nationals and Blocked Persons (SDN)',
          slug: 'us-ofac-specially-designated-nationals-and-blocked-persons',
          url: 'http://bit.ly/1MLgpye',
        },
      ],
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_136',
    type: ApiModel.ReportCryptoAddressWatchlist,
  };
}

export function createPreviewReportSentilinkApplicationRisk(
  attributes?: Partial<ReportSentilinkApplicationRisk>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportSentilinkApplicationRisk {
  return {
    attributes: {
      'primary-input': 'Alexander Sample',
      'completed-at': completedAt,
      'created-at': createdAt,
      status: ReportStatus.Ready,
      query: {
        'user-id': 'sample_id',
        'name-first': 'Alexander',
        'name-last': 'Sample',
        birthdate: '1987-10-31',
        'address-country-code': 'US',
        'address-street-1': '600 California St',
        'address-street-2': '11F',
        'address-city': 'San Francisco',
        'address-subdivision': 'California',
        'address-postal-code': '065100000',
        'social-security-number': '123456789',
      },
      result: {
        'application-id': 'sample_id',
        'customer-id': 'sample_id',
        environment: SentilinkEnvironmentType.Sandbox,
        'sentilink-synthetic-score': {
          score: 100,
          version: '1.2.3',
        },
        'sentilink-id-theft-score': {
          score: 200,
          version: '1.2.4',
        },
        status: 'passed',
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_123',
    type: ApiModel.ReportSentilinkApplicationRisk,
  };
}

export function createPreviewReportSentilinkScores(
  attributes?: Partial<ReportSentilinkScores>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportSentilinkScores {
  return {
    attributes: {
      'primary-input': 'Alexander Sample',
      'completed-at': completedAt,
      'created-at': createdAt,
      status: ReportStatus.Ready,
      query: {
        'user-id': 'sample_id',
        'name-first': 'Alexander',
        'name-last': 'Sample',
        birthdate: '1987-10-31',
        'address-country-code': 'US',
        'address-street-1': '600 California St',
        'address-street-2': '11F',
        'address-city': 'San Francisco',
        'address-subdivision': 'California',
        'address-postal-code': '065100000',
        'social-security-number': '123456789',
      },
      result: {
        'application-id': 'sample_id',
        'customer-id': 'sample_id',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        environment: null,
        notes: 'notes',
        scores: [
          {
            name: 'sentilink_abuse_score',
            score: 100,
            version: 'v_1',
          },
          {
            name: 'sentilink_id_theft_score',
            score: 200,
            version: 'v_1',
          },
          {
            name: 'sentilink_first_party_synthetic_score',
            score: 450,
            version: 'v_1',
          },
          {
            name: 'sentilink_third_party_synthetic_score',
            score: 600,
            version: 'v_1',
          },
        ],
        status: 'sample_status',
        'transaction-id': 'transaction_id',
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_123',
    type: ApiModel.ReportSentilinkScores,
  };
}

export function createPreviewReportMxAccount(
  attributes?: Partial<ReportMxAccount>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportMxAccount {
  return {
    attributes: {
      'primary-input': 'Alexander Sample Jack',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      query: {
        'user-guid': 'USR-sampleid',
        'member-guid': 'MEM-sampleid',
        'name-first': 'Alexander',
        'name-last': 'Sample',
        'name-middle': 'Jack',
      },
      result: {
        'account-owner-results': [
          {
            'account-guid': 'ACT-123',
            address: '600 California St',
            city: 'San Francisco',
            country: 'US',
            email: 'sample@withpersona.com',
            guid: 'ACT-sampleid',
            'member-guid': 'MEM-sampleid',
            'owner-name': 'Sample Name',
            phone: '123456789',
            'postal-code': '601223',
            state: 'CA',
            'user-guid': 'USR-sample123id',
            'is-match': true,
          },
          {
            'account-guid': 'ACT-123',
            address: '600 California St',
            city: 'San Francisco',
            country: 'US',
            email: 'sample@withpersona.com',
            guid: 'ACT-sampleid',
            'member-guid': 'MEM-sample123id',
            'owner-name': 'Sample None Matched Name',
            phone: '123456789',
            'postal-code': '601223',
            state: 'CA',
            'user-guid': 'USR-sample123id',
            'is-match': false,
          },
          {
            'account-guid': 'ACT-123',
            address: '600 California St',
            city: 'San Francisco',
            country: 'US',
            email: 'sample@withpersona.com',
            guid: 'ACT-sampleid',
            'member-guid': 'MEM-sample123id',
            'owner-name': 'Sample None Matched Name',
            phone: '123456789',
            'postal-code': '601223',
            state: 'CA',
            'user-guid': 'USR-sample123id',
            'is-match': false,
          },
        ],
        status: ResultStatus.PASSED,
      },
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_123',
    type: ApiModel.ReportMxAccount,
  };
}

export function createPreviewReportCustomList(
  attributes?: Partial<ReportCustomList>,
  relationships?: Partial<ReportRelationships>,
  id?: string,
): ReportCustomList {
  return {
    attributes: {
      'primary-input': 'Alexander Sample Jack',
      status: ReportStatus.Ready,
      'completed-at': completedAt,
      'created-at': createdAt,
      'latest-published-version-id': 'testing',
      'scheduled-at': scheduledAt,
      'hit-entries': [],
      'dismissed-entries': [],
      query: {
        'name-first': 'Alexander',
        'name-last': 'Sample',
        'name-middle': 'Jack',
      },
      result: ReportRunMatchStatus.NoMatch,
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'report-template': null,
      tags: {
        data: [],
      },
      ...relationships,
    },
    id: id || 'rep_123',
    type: ApiModel.ReportCustomList,
  };
}
